import {
  Component,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
  input,
  output,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslocoService} from '@jsverse/transloco';
import {catchError, finalize, of, Subscription} from 'rxjs';
import {DirectusSelect, DirectusSelectValue, DirectusService} from '@frontend/directus';
import {Select, SelectChangeEvent} from 'primeng/select';
import {FixedValueService} from '../../../services/fixed-value.service';

export interface DirectusSelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-directus-select',
  templateUrl: './directus-select.component.html',
  imports: [FormsModule, Select],
})
export class DirectusSelectComponent implements OnInit, OnChanges, OnDestroy {
  readonly dropdownKey = input('');
  readonly placeholder = input('');
  readonly disabled = input(false);
  readonly parentValue = input<string | null>(null);
  readonly selected = input<string | null>(null);
  readonly projectId = input.required<number>();
  readonly useStoredValues = input(false);
  readonly preloadedDropdown = input<DirectusSelect | null>(null);
  readonly appendTo = input<string | null>(null);
  readonly filter = input(false);

  readonly valueChange = output<string>();
  readonly selectionChange = output<DirectusSelectOption>();

  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly fixedValueService = inject(FixedValueService);

  options = signal<DirectusSelectOption[]>([]);
  selectedValue = signal<string | null>(null);
  isLoading = signal<boolean>(false);

  private dropdown: DirectusSelect | null = null;
  private langSub: Subscription | undefined;

  ngOnInit(): void {
    this.loadDropdownData();
    this.langSub = this.translocoService.langChanges$.subscribe(() => {
      this.updateOptions();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['dropdownKey'] && !changes['dropdownKey'].firstChange) ||
      (changes['projectId'] && !changes['projectId'].firstChange) ||
      (changes['useStoredValues'] && !changes['useStoredValues'].firstChange) ||
      (changes['preloadedDropdown'] && !changes['preloadedDropdown'].firstChange)
    ) {
      this.loadDropdownData();
    }

    if (changes['parentValue'] && this.dropdown) {
      this.updateOptions();
    }

    const selected = this.selected();
    if (changes['selected'] && selected !== undefined) {
      this.selectedValue.set(selected);
    }
  }

  private loadDropdownData(): void {
    const preloadedDropdown = this.preloadedDropdown();
    if (preloadedDropdown) {
      this.applyDropdownData(preloadedDropdown);
      return;
    }

    const dropdownKey = this.dropdownKey();
    if (!dropdownKey) {
      console.error('No dropdown key provided for directus-select component');
      return;
    }

    this.isLoading.set(true);

    const observable = this.useStoredValues()
      ? this.fixedValueService.loadOrInitializeFixedDropdownValues(this.projectId(), dropdownKey)
      : this.directusService.loadDropdownWithValues(dropdownKey);

    observable
      .pipe(
        catchError((error) => {
          console.error(`Error loading dropdown data for key ${this.dropdownKey()}:`, error);
          return of(null as unknown as DirectusSelect);
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe((dropdown) => {
        if (dropdown) {
          this.applyDropdownData(dropdown);
        }
      });
  }

  private applyDropdownData(dropdown: DirectusSelect): void {
    this.dropdown = dropdown;
    this.updateOptions();

    const selected = this.selected();
    if (selected && !this.selectedValue()) {
      this.selectedValue.set(selected);
    }
  }

  private updateOptions(): void {
    if (!this.dropdown) return;

    const activeLang = this.translocoService.getActiveLang();
    let filteredValues: DirectusSelectValue[] = this.dropdown.values;

    if (this.parentValue() !== null) {
      filteredValues = filteredValues.filter((v) => v.parentValue === this.parentValue());
    }

    const newOptions = filteredValues.map((value) => {
      const translation =
        value.translations.find((t) => t.language === activeLang) ||
        value.translations.find((t) => t.language === 'en') ||
        value.translations[0];

      return {
        label: translation ? translation.name : `Option ${value.id}`,
        value: value.id,
      };
    });

    this.options.set(newOptions);

    if (this.selectedValue() && !newOptions.some((opt) => opt.value === this.selectedValue())) {
      this.selectedValue.set(null);
    }
  }

  onSelectionChange(event: SelectChangeEvent): void {
    this.selectedValue.set(event.value);

    const selectedOption = this.options().find((opt) => opt.value === event.value);

    if (selectedOption) {
      this.selectionChange.emit(selectedOption);
    }

    this.valueChange.emit(event.value);
  }

  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
  }
}
